/**
 * RouteMap - Google Maps integration for route visualization
 *
 * Features:
 * - Real coordinates from Latitude__c/Longitude__c fields
 * - Google DirectionsService for actual driving routes
 * - Depot marker (start/end point)
 * - Numbered stop markers with completion status
 * - Click to select stop
 * - Time window violation highlighting
 *
 * TODO: Migrate from deprecated Marker to AdvancedMarkerElement when:
 * - A Map ID is configured in Google Cloud Console
 * - Custom marker styling with HTML/CSS is implemented
 * See: https://developers.google.com/maps/documentation/javascript/advanced-markers
 */

import { useMemo, useCallback, useState, useEffect } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
  InfoWindow,
} from '@react-google-maps/api';
import { useRoutePlannerStore } from '../../stores/route-planner-store';
import { useRoutePlannerDrawerStore } from '../../stores/route-planner-drawer-store';
import { GOOGLE_MAPS_CONFIG, salesforceClient } from '../../lib/salesforce';
import { Spinner } from '../ui';
import type { RouteStop, Depot, UnassignedWerkOrder, GeocodeAdresResultaat } from '../../lib/salesforce/types';
import { formatTijd } from '../../lib/format';

// ============================================================================
// CONSTANTS
// ============================================================================

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

// Default depot location (Havelte) - used as fallback
const DEFAULT_DEPOT: Depot = {
  address: 'Oeveraseweg 13, 7971 PA Havelte',
  latitude: 52.7582089,
  longitude: 6.2362659
};

// Max waypoints for DirectionsService (23 intermediate + origin + destination = 25)
const MAX_DIRECTIONS_WAYPOINTS = 23;

// Zoomniveau waarop we inzoomen na een adres-zoekopdracht (straatniveau)
const ADRES_ZOEK_ZOOM = 16;

/** Precisie van de geocode in gewone taal. Een APPROXIMATE-hit is het midden
 *  van een straat of wijk, geen deur. Dat moet je zien voordat je erop plant. */
function precisieLabel(locationType: string | null): string | null {
  switch (locationType) {
    case 'ROOFTOP':
      return null; // exact, geen waarschuwing nodig
    case 'RANGE_INTERPOLATED':
      return 'Bij benadering (tussen twee huisnummers)';
    case 'GEOMETRIC_CENTER':
      return 'Midden van de straat, geen exact huisnummer';
    default:
      return 'Bij benadering, controleer het huisnummer';
  }
}

// ============================================================================
// TYPES
// ============================================================================

interface StopMarker {
  id: string;
  position: google.maps.LatLngLiteral;
  sequence: number;
  isSelected: boolean;
  isCompleted: boolean;
  isTimeWindowViolation: boolean;
  stopType: 'Ophalen' | 'Terugbrengen';
  accountName: string | null;
  arrivalTime: string | null;
}

interface UnassignedMarker {
  id: string;
  position: google.maps.LatLngLiteral;
  werkOrder: UnassignedWerkOrder;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function RouteMap() {
  const {
    stops,
    depot,
    selectedStopId,
    setSelectedStop,
    directionsResult,
    setDirectionsResult,
    mapCenter,
    mapZoom,
    unassignedWerkOrders,
    showUnassignedOnMap,
    showOphalenOnMap,
    showAfleverenOnMap,
    toggleShowUnassignedOnMap,
    toggleShowOphalenOnMap,
    toggleShowAfleverenOnMap,
    addStopFromUnassigned,
  } = useRoutePlannerStore();

  const openDrawer = useRoutePlannerDrawerStore((s) => s.open);

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [infoWindowStopId, setInfoWindowStopId] = useState<string | null>(null);
  const [infoWindowUnassignedId, setInfoWindowUnassignedId] = useState<string | null>(null);
  const [directionsError, setDirectionsError] = useState<string | null>(null);
  const [addingUnassignedId, setAddingUnassignedId] = useState<string | null>(null);
  const [unassignedPhoneCopiedId, setUnassignedPhoneCopiedId] = useState<string | null>(null);
  // For routes with >23 waypoints, we split into multiple DirectionsService calls
  const [chunkedDirectionsResults, setChunkedDirectionsResults] = useState<google.maps.DirectionsResult[]>([]);
  // Vrij ingetypt adres opzoeken op de kaart (geen route-mutatie, alleen kijken)
  const [adresInvoer, setAdresInvoer] = useState('');
  const [adresTreffer, setAdresTreffer] = useState<GeocodeAdresResultaat | null>(null);
  const [adresBezig, setAdresBezig] = useState(false);
  const [adresFout, setAdresFout] = useState<string | null>(null);
  const [adresInfoOpen, setAdresInfoOpen] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
  });

  // Use depot from store or default
  const depotLocation = useMemo(() => ({
    lat: depot?.latitude ?? DEFAULT_DEPOT.latitude,
    lng: depot?.longitude ?? DEFAULT_DEPOT.longitude,
  }), [depot]);

  // Create markers for each stop with real coordinates
  const markers: StopMarker[] = useMemo(() => {
    return stops
      .filter(stop => stop.latitude !== null && stop.longitude !== null)
      .map((stop, index) => ({
        id: stop.id,
        position: {
          lat: stop.latitude!,
          lng: stop.longitude!,
        },
        sequence: stop.routeSequence ?? index + 1,
        isSelected: selectedStopId === stop.id,
        isCompleted: stop.isCompleted,
        isTimeWindowViolation: !stop.isWithinTimeWindow,
        stopType: stop.stopType,
        accountName: stop.accountName,
        arrivalTime: stop.arrivalTime,
      }));
  }, [stops, selectedStopId]);

  // Warn when stops are filtered out due to missing coordinates
  const missingCoordsCount = stops.length - markers.length;
  useEffect(() => {
    if (missingCoordsCount > 0) {
      console.warn(
        `[RouteMap] ${missingCoordsCount} stop(s) zonder coords gefilterd uit kaart — gebruik "Geocode ontbrekende" om te fixen`
      );
    }
  }, [missingCoordsCount]);

  // Get stops with valid coordinates for directions
  const stopsWithCoords = useMemo(() =>
    stops.filter(s => s.latitude !== null && s.longitude !== null),
    [stops]
  );

  // Create a stable key that changes when stop order changes (for triggering directions recalc)
  const stopsOrderKey = useMemo(() =>
    stopsWithCoords.map(s => s.id).join(','),
    [stopsWithCoords]
  );

  // Create markers for unassigned werk orders
  const unassignedMarkers: UnassignedMarker[] = useMemo(() => {
    if (!showUnassignedOnMap) return [];
    return unassignedWerkOrders
      .filter(wo => wo.latitude !== null && wo.longitude !== null)
      .filter(wo => {
        if (wo.type === 'Ophalen' && !showOphalenOnMap) return false;
        if (wo.type === 'Terugbrengen' && !showAfleverenOnMap) return false;
        return true;
      })
      .map(wo => ({
        id: wo.id,
        position: {
          lat: wo.latitude!,
          lng: wo.longitude!,
        },
        werkOrder: wo,
      }));
  }, [unassignedWerkOrders, showUnassignedOnMap, showOphalenOnMap, showAfleverenOnMap]);

  // Calculate directions when stops change
  useEffect(() => {
    if (!isLoaded || !mapRef || stopsWithCoords.length === 0) {
      return;
    }

    const directionsService = new google.maps.DirectionsService();

    // Build waypoints - only use stops that have coordinates
    let waypoints: google.maps.DirectionsWaypoint[] = stopsWithCoords.map(stop => ({
      location: new google.maps.LatLng(stop.latitude!, stop.longitude!),
      stopover: true,
    }));

    // If more than max waypoints, split into chunks and make multiple DirectionsService calls
    if (waypoints.length > MAX_DIRECTIONS_WAYPOINTS) {
      console.log(`Route has ${waypoints.length} waypoints, splitting into chunks for DirectionsService`);
      setDirectionsResult(null);
      setDirectionsError(null);

      // Split waypoints into chunks of max MAX_DIRECTIONS_WAYPOINTS
      // Each chunk needs to connect to the next, so we overlap the last point of each chunk
      const allPoints = [
        depotLocation,
        ...stopsWithCoords.map(stop => ({ lat: stop.latitude!, lng: stop.longitude! })),
        depotLocation,
      ];

      const chunks: google.maps.LatLngLiteral[][] = [];
      const chunkSize = MAX_DIRECTIONS_WAYPOINTS + 1; // +1 because we need origin and destination per chunk

      for (let i = 0; i < allPoints.length - 1; i += chunkSize - 1) {
        const chunk = allPoints.slice(i, Math.min(i + chunkSize, allPoints.length));
        if (chunk.length >= 2) {
          chunks.push(chunk);
        }
      }

      console.log(`Split into ${chunks.length} chunks`);

      // Make DirectionsService calls for each chunk
      const results: google.maps.DirectionsResult[] = [];
      let hasError = false;

      const processChunk = (chunkIndex: number) => {
        if (chunkIndex >= chunks.length || hasError) {
          if (!hasError) {
            console.log(`All ${results.length} direction chunks loaded successfully`);
            setChunkedDirectionsResults(results);
          }
          return;
        }

        const chunk = chunks[chunkIndex];
        if (!chunk || chunk.length < 2) {
          // Skip invalid chunks
          processChunk(chunkIndex + 1);
          return;
        }

        const origin = chunk[0]!;
        const destination = chunk[chunk.length - 1]!;
        const chunkWaypoints = chunk.slice(1, -1).map(point => ({
          location: new google.maps.LatLng(point.lat, point.lng),
          stopover: true,
        }));

        const request: google.maps.DirectionsRequest = {
          origin,
          destination,
          waypoints: chunkWaypoints,
          optimizeWaypoints: false,
          travelMode: google.maps.TravelMode.DRIVING,
          region: 'NL',
        };

        directionsService.route(request, (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            results.push(result);
            // Process next chunk with a small delay to avoid rate limiting
            setTimeout(() => processChunk(chunkIndex + 1), 100);
          } else {
            console.error(`Directions chunk ${chunkIndex} failed:`, status);
            hasError = true;
            setDirectionsError(`Route berekening mislukt voor segment ${chunkIndex + 1}: ${status}`);
          }
        });
      };

      // Start processing chunks
      processChunk(0);
      return;
    }

    // Clear chunked results when using single DirectionsService call
    setChunkedDirectionsResults([]);

    // Origin is always depot
    const origin = depotLocation;
    // Destination is always depot (round trip)
    const destination = depotLocation;

    // Remove first waypoint if it's the origin
    if (waypoints.length > 0) {
      const request: google.maps.DirectionsRequest = {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: false, // Keep our order
        travelMode: google.maps.TravelMode.DRIVING,
        region: 'NL',
      };

      directionsService.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          setDirectionsResult(result);
          setDirectionsError(null);
        } else {
          console.error('Directions request failed:', status);
          setDirectionsError(`Route berekening mislukt: ${status}`);
        }
      });
    }
  }, [isLoaded, mapRef, stopsOrderKey, depotLocation.lat, depotLocation.lng, stopsWithCoords]);

  // Fit bounds to show all markers - only for markers within Netherlands
  useEffect(() => {
    if (!mapRef || markers.length === 0) return;

    // Netherlands bounding box (approximate)
    const NL_BOUNDS = {
      minLat: 50.5,
      maxLat: 53.8,
      minLng: 3.2,
      maxLng: 7.5
    };

    // Filter markers that are within Netherlands
    const nlMarkers = markers.filter(m =>
      m.position.lat >= NL_BOUNDS.minLat &&
      m.position.lat <= NL_BOUNDS.maxLat &&
      m.position.lng >= NL_BOUNDS.minLng &&
      m.position.lng <= NL_BOUNDS.maxLng
    );

    // Only fit bounds if we have valid NL markers
    if (nlMarkers.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    // Only include depot if it's within Netherlands
    if (depotLocation.lat >= NL_BOUNDS.minLat &&
        depotLocation.lat <= NL_BOUNDS.maxLat &&
        depotLocation.lng >= NL_BOUNDS.minLng &&
        depotLocation.lng <= NL_BOUNDS.maxLng) {
      bounds.extend(depotLocation);
    }

    nlMarkers.forEach(m => bounds.extend(m.position));

    mapRef.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, markers.length]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    setMapRef(map);
  }, []);

  const handleMarkerClick = useCallback((stopId: string) => {
    setSelectedStop(stopId);
    setInfoWindowStopId(stopId);
    setInfoWindowUnassignedId(null);
  }, [setSelectedStop]);

  const handleInfoWindowClose = useCallback(() => {
    setInfoWindowStopId(null);
    setInfoWindowUnassignedId(null);
  }, []);

  // Adres opzoeken: geocoden via het office-serverpad, pin zetten, inzoomen.
  const handleAdresZoek = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const adres = adresInvoer.trim();
    if (adres === '' || adresBezig) return;

    setAdresBezig(true);
    setAdresFout(null);
    try {
      const treffer = await salesforceClient.geocodeAdres(adres);
      setAdresTreffer(treffer);
      setAdresInfoOpen(true);
      setInfoWindowStopId(null);
      setInfoWindowUnassignedId(null);
      if (mapRef) {
        mapRef.panTo({ lat: treffer.lat, lng: treffer.lng });
        mapRef.setZoom(ADRES_ZOEK_ZOOM);
      }
    } catch (err) {
      setAdresTreffer(null);
      setAdresInfoOpen(false);
      setAdresFout(err instanceof Error ? err.message : 'Adres niet gevonden');
    } finally {
      setAdresBezig(false);
    }
  }, [adresInvoer, adresBezig, mapRef]);

  const handleAdresWissen = useCallback(() => {
    setAdresInvoer('');
    setAdresTreffer(null);
    setAdresFout(null);
    setAdresInfoOpen(false);
  }, []);

  // Get marker color based on state
  const getMarkerColor = (marker: StopMarker): string => {
    if (marker.isCompleted) return '#8b5cf6'; // violet-500 (duidelijk onderscheid van Afleveren)
    if (marker.isTimeWindowViolation) return '#f59e0b'; // amber-500
    if (marker.isSelected) return '#2b7c9c'; // cinco-primary
    if (marker.stopType === 'Terugbrengen') return '#10b981'; // emerald-500
    return '#3b82f6'; // blue-500
  };

  // Get marker scale based on state
  const getMarkerScale = (marker: StopMarker): number => {
    return marker.isSelected ? 14 : 10;
  };

  // Get stop by ID for info window
  const getStopById = (id: string): RouteStop | undefined =>
    stops.find(s => s.id === id);

  // Get unassigned werk order by ID for info window
  const getUnassignedById = (id: string): UnassignedWerkOrder | undefined =>
    unassignedWerkOrders.find(wo => wo.id === id);

  // Handle click on unassigned marker
  const handleUnassignedMarkerClick = useCallback((woId: string) => {
    setInfoWindowUnassignedId(woId);
    setInfoWindowStopId(null);
  }, []);

  // Copy unassigned WO phone with per-WO feedback
  const handleCopyUnassignedPhone = useCallback((woId: string, phone: string) => {
    navigator.clipboard.writeText(phone);
    setUnassignedPhoneCopiedId(woId);
    setTimeout(() => setUnassignedPhoneCopiedId(prev => (prev === woId ? null : prev)), 2000);
  }, []);

  // Handle adding unassigned to route
  const handleAddUnassignedToRoute = useCallback(async (werkOrder: UnassignedWerkOrder) => {
    setAddingUnassignedId(werkOrder.id);
    try {
      await addStopFromUnassigned(werkOrder);
      setInfoWindowUnassignedId(null);
    } finally {
      setAddingUnassignedId(null);
    }
  }, [addStopFromUnassigned]);

  if (loadError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100">
        <p className="text-error">Fout bij laden van Google Maps</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={mapZoom}
        options={mapOptions}
        onLoad={handleMapLoad}
      >
        {/* Render directions if available */}
        {directionsResult && (
          <DirectionsRenderer
            directions={directionsResult}
            options={{
              suppressMarkers: true, // We render our own markers
              polylineOptions: {
                strokeColor: '#2b7c9c',
                strokeOpacity: 0.8,
                strokeWeight: 4,
              },
            }}
          />
        )}

        {/* Render chunked directions for routes with >23 waypoints */}
        {chunkedDirectionsResults.map((result, index) => (
          <DirectionsRenderer
            key={`chunk-${index}`}
            directions={result}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#2b7c9c',
                strokeOpacity: 0.8,
                strokeWeight: 4,
              },
            }}
          />
        ))}

        {/* Depot marker */}
        <Marker
          position={depotLocation}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: '#1e293b',
            fillOpacity: 1,
            strokeWeight: 3,
            strokeColor: '#ffffff',
          }}
          title="Depot - Havelte"
          zIndex={1000}
        />

        {/* Stop markers */}
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            onClick={() => handleMarkerClick(marker.id)}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: getMarkerScale(marker),
              fillColor: getMarkerColor(marker),
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: '#ffffff',
            }}
            label={{
              text: String(marker.sequence),
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 'bold',
            }}
            zIndex={marker.isSelected ? 999 : marker.sequence}
          />
        ))}

        {/* Unassigned werk order markers (gray, smaller) */}
        {unassignedMarkers.map((um) => (
          <Marker
            key={`unassigned-${um.id}`}
            position={um.position}
            onClick={() => handleUnassignedMarkerClick(um.id)}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: um.werkOrder.type === 'Ophalen' ? '#93c5fd' : '#86efac', // light blue/green
              fillOpacity: 0.8,
              strokeWeight: 2,
              strokeColor: um.werkOrder.type === 'Ophalen' ? '#3b82f6' : '#22c55e',
            }}
            zIndex={10}
          />
        ))}

        {/* Gezocht adres: losse pin, hoort bij geen enkele stop */}
        {adresTreffer && (
          <Marker
            position={{ lat: adresTreffer.lat, lng: adresTreffer.lng }}
            onClick={() => setAdresInfoOpen(true)}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: '#e11d48', // rose-600
              fillOpacity: 1,
              strokeWeight: 3,
              strokeColor: '#ffffff',
            }}
            title={adresTreffer.adres}
            zIndex={2000}
          />
        )}

        {/* Info window voor het gezochte adres */}
        {adresTreffer && adresInfoOpen && (
          <InfoWindow
            position={{ lat: adresTreffer.lat, lng: adresTreffer.lng }}
            onCloseClick={() => setAdresInfoOpen(false)}
          >
            <div className="p-1 min-w-[200px] max-w-[260px]">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-rose-100 text-rose-800">
                  Gezocht adres
                </span>
              </div>
              <p className="font-medium text-sm">
                {adresTreffer.formattedAddress ?? adresTreffer.adres}
              </p>
              {adresTreffer.formattedAddress &&
                adresTreffer.formattedAddress !== adresTreffer.adres && (
                  <p className="text-xs text-gray-500">Gezocht: {adresTreffer.adres}</p>
                )}
              <p className="text-xs text-gray-500 mt-1">
                {adresTreffer.lat.toFixed(5)}, {adresTreffer.lng.toFixed(5)}
              </p>
              {precisieLabel(adresTreffer.locationType) && (
                <p className="text-xs text-amber-600 mt-1">
                  {precisieLabel(adresTreffer.locationType)}
                </p>
              )}
            </div>
          </InfoWindow>
        )}

        {/* Info window for selected marker */}
        {infoWindowStopId && (() => {
          const stop = getStopById(infoWindowStopId);
          const marker = markers.find(m => m.id === infoWindowStopId);
          if (!stop || !marker) return null;

          return (
            <InfoWindow
              position={marker.position}
              onCloseClick={handleInfoWindowClose}
            >
              <div className="p-1 min-w-[180px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                    stop.stopType === 'Ophalen'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {stop.stopType}
                  </span>
                  <span className="text-xs text-gray-500">#{marker.sequence}</span>
                </div>
                <p className="font-medium text-sm">{stop.accountName}</p>
                <p className="text-xs text-gray-600">{stop.fullAddress}</p>
                {stop.arrivalTime && (
                  <p className="text-xs text-gray-500 mt-1">
                    Aankomst: {formatTijd(stop.arrivalTime)}
                  </p>
                )}
                {stop.timeFrom && stop.timeTo && (
                  <p className={`text-xs mt-0.5 ${
                    stop.isWithinTimeWindow ? 'text-gray-500' : 'text-amber-600 font-medium'
                  }`}>
                    Tijdsvenster: {stop.timeFrom} - {stop.timeTo}
                    {!stop.isWithinTimeWindow && ' ⚠️'}
                  </p>
                )}
              </div>
            </InfoWindow>
          );
        })()}

        {/* Info window for unassigned werk order */}
        {infoWindowUnassignedId && (() => {
          const wo = getUnassignedById(infoWindowUnassignedId);
          const um = unassignedMarkers.find(m => m.id === infoWindowUnassignedId);
          if (!wo || !um) return null;

          const phoneCopied = unassignedPhoneCopiedId === wo.id;

          return (
            <InfoWindow
              position={um.position}
              onCloseClick={handleInfoWindowClose}
            >
              <div className="p-1 min-w-[220px] max-w-[280px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                    wo.type === 'Ophalen'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {wo.type}
                  </span>
                  <span className="text-xs text-gray-400">Niet ingepland</span>
                </div>
                <p className="font-medium text-sm">{wo.accountName}</p>
                <p className="text-xs text-gray-600">
                  {wo.fullAddress}
                </p>
                <p className="text-xs text-gray-500 mt-1">WO: {wo.name}</p>

                {/* Opmerkingen — altijd zichtbaar */}
                {wo.type === 'Ophalen' && wo.opmerkingenOphalen && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-800 text-xs">
                    <strong>Ophalen:</strong> {wo.opmerkingenOphalen}
                  </div>
                )}
                {wo.type === 'Terugbrengen' && wo.opmerkingenAfleveren && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-xs">
                    <strong>Afleveren:</strong> {wo.opmerkingenAfleveren}
                  </div>
                )}
                {wo.opmerkingenPlanning && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
                    <strong>Planning:</strong> {wo.opmerkingenPlanning}
                  </div>
                )}

                {/* Action icons */}
                <div className="flex items-center gap-1 mt-2">
                  {/* Phone copy button met feedback */}
                  {wo.contactPhone && (
                    <button
                      type="button"
                      onClick={() => handleCopyUnassignedPhone(wo.id, wo.contactPhone!)}
                      className={`p-1.5 rounded transition-colors ${
                        phoneCopied
                          ? 'text-green-600 bg-green-50'
                          : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title={phoneCopied ? 'Gekopieerd!' : `Kopieer ${wo.contactPhone}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </button>
                  )}

                  {/* Sidebar opener — open WO in drawer */}
                  <button
                    type="button"
                    onClick={() => openDrawer({ type: 'order', id: wo.id })}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    title="Open werkorder in zijbalk"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 4v16" />
                    </svg>
                  </button>
                </div>

                <button
                  onClick={() => handleAddUnassignedToRoute(wo)}
                  disabled={addingUnassignedId === wo.id}
                  className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingUnassignedId === wo.id ? 'Toevoegen...' : 'Toevoegen aan route'}
                </button>
              </div>
            </InfoWindow>
          );
        })()}
      </GoogleMap>

      {/* Adres zoeken: vrij adres op de kaart tonen, raakt de route niet */}
      <div className="absolute top-4 left-4 w-72">
        <form
          onSubmit={handleAdresZoek}
          className="flex items-center gap-1 bg-white rounded-lg shadow-md px-2 py-1.5"
        >
          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
          <input
            type="text"
            value={adresInvoer}
            onChange={(e) => setAdresInvoer(e.target.value)}
            placeholder="Adres zoeken op de kaart"
            aria-label="Adres zoeken op de kaart"
            className="flex-1 min-w-0 text-sm bg-transparent outline-none placeholder:text-gray-400"
          />
          {(adresInvoer !== '' || adresTreffer) && (
            <button
              type="button"
              onClick={handleAdresWissen}
              title="Wissen"
              aria-label="Zoekopdracht wissen"
              className="p-1 text-gray-400 hover:text-gray-600 rounded shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <button
            type="submit"
            disabled={adresBezig || adresInvoer.trim() === ''}
            className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            {adresBezig ? 'Bezig…' : 'Zoek'}
          </button>
        </form>
        {adresFout && (
          <div className="mt-1 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5 rounded-md text-xs shadow-sm">
            {adresFout}
          </div>
        )}
        {adresTreffer && !adresFout && (
          <button
            type="button"
            onClick={() => {
              setAdresInfoOpen(true);
              if (mapRef) {
                mapRef.panTo({ lat: adresTreffer.lat, lng: adresTreffer.lng });
                mapRef.setZoom(ADRES_ZOEK_ZOOM);
              }
            }}
            className="mt-1 w-full text-left bg-white border border-gray-200 px-2 py-1.5 rounded-md text-xs shadow-sm hover:bg-gray-50"
          >
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-600 shrink-0" />
              <span className="text-gray-700 truncate">
                Terug naar {adresTreffer.formattedAddress ?? adresTreffer.adres}
              </span>
            </span>
          </button>
        )}
      </div>

      {/* Directions error message */}
      {directionsError && (
        <div className="absolute bottom-4 left-4 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-md text-sm shadow-sm">
          {directionsError}
        </div>
      )}

      {/* Legend */}
      <div className="absolute top-4 right-4 bg-white rounded-lg shadow-md px-3 py-2 text-xs space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-slate-800" />
          <span>Depot</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-blue-500" />
          <span>Ophalen</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-emerald-500" />
          <span>Afleveren</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-violet-500" />
          <span>Afgerond</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-amber-500" />
          <span>Buiten tijdsvenster</span>
        </div>
        {adresTreffer && (
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-rose-600" />
            <span>Gezocht adres</span>
          </div>
        )}

        {/* Toggles for unassigned werk orders */}
        <hr className="my-2 border-gray-200" />
        <div className="font-medium text-gray-700 mb-1">Niet-ingepland</div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showUnassignedOnMap}
            onChange={toggleShowUnassignedOnMap}
            className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-gray-600">
            Toon op kaart {unassignedWerkOrders.length > 0 && `(${unassignedWerkOrders.length})`}
          </span>
        </label>
        {showUnassignedOnMap && (
          <div className="ml-4 space-y-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOphalenOnMap}
                onChange={toggleShowOphalenOnMap}
                className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="w-2 h-2 rounded-full bg-blue-300" />
              <span className="text-gray-500">
                Ophalen ({unassignedWerkOrders.filter(wo => wo.type === 'Ophalen').length})
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAfleverenOnMap}
                onChange={toggleShowAfleverenOnMap}
                className="w-3 h-3 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="w-2 h-2 rounded-full bg-green-300" />
              <span className="text-gray-500">
                Afleveren ({unassignedWerkOrders.filter(wo => wo.type === 'Terugbrengen').length})
              </span>
            </label>
          </div>
        )}
      </div>

      {/* No coordinates warning */}
      {stops.length > 0 && markers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80">
          <div className="bg-white p-4 rounded-lg shadow-lg text-center max-w-sm">
            <p className="text-gray-700 font-medium mb-2">
              Geen coördinaten beschikbaar
            </p>
            <p className="text-sm text-gray-500">
              De stops hebben nog geen GPS coördinaten.
              Klik op "Optimaliseren" om de route te berekenen.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
